import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { useCSVDownloader } from 'react-papaparse';
import { isEmpty, orderBy, uniqBy } from 'lodash';
import styled, { useTheme } from 'styled-components';
import { Col, Divider, Row, Upload } from 'antd5';
import { DownloadOutlined, InfoCircleTwoTone, UploadOutlined } from '@ant-design5/icons';
import { Button, WarningAlert, useMessage } from 'components/ui';

import { useServerDataTableContext } from 'components/DataTable/ServerDataTable';
import { uploadIngestFiles, useGetIngestUploadUrls } from 'utils/ingest';
import { useUploadContext } from 'components/FileUploader';
import { CSV_BULK_UPLOAD_OPTIONS, JSON_BULK_UPLOAD_OPTIONS } from 'utils/constants';
import { EXAMPLE_INSPECTIONS, ExampleInspection } from '../constants';
import { FieldMappingForm } from '../FieldMappingForm';
import { Preview } from './Preview';
import { getBulkUploadEventTypes } from '../../utils';
import { InspectionUploadWorkflowComponentProps, Steps } from '../UploadInspectionDataModal';
import { ValidationProps } from './Validation';
import {
  AtlasGqlDecimalSeparator,
  AtlasGqlFileFieldDelimiter,
  AtlasGqlFileFormat,
  AtlasGqlInspectionTask,
  AtlasGqlInspectionType,
  AtlasGqlMetadataFieldMapping,
  AtlasGqlSite,
  AtlasGqlTurbine,
  useGetInspectionMetadataFieldsQuery,
  useGetTasksForInspectionUploadQuery,
  useProcessInspectionMetadataFileMutation,
} from 'types/atlas-graphql';
import { getUniqueIdentifier } from 'horizon/components/Assets/AssetDetails/helpers';
import { useUploadInspectionDataModalContext } from '../Context/UploadInspectionDataModalContext';
import { flatParseJSON, getProvidedFieldNamesFromJsonRecords, parseCsv } from '../utils';

const INSPECTION_UPLOAD_LIMIT = 150;

export const MultipleInspections: React.FC<
  InspectionUploadWorkflowComponentProps & {
    inspectionType?: AtlasGqlInspectionType;
    setValidationStepProps: Dispatch<
      SetStateAction<
        Omit<ValidationProps, 'setStep' | 'setPendingStep' | 'setErroredUploadSteps' | 'rollback'>
      >
    >;
  }
> = ({
  uploadId,
  requireTaskId,
  step,
  setStep,
  setValidationStepProps,
  inspectionTasks,
  inspectionType,
}) => {
  const theme = useTheme();
  const [file, setFile] = useState<File>();
  const [providedFieldNames, setProvidedFieldNames] = useState<string[]>();
  const [previewRecords, setPreviewRecords] = useState<{ [key: string]: string }[]>([]);
  const [fieldMappings, setFieldMappings] = useState<AtlasGqlMetadataFieldMapping[] | undefined>();
  const [inspectionCount, setInspectionCount] = useState<number>(0);
  const [fileType, setFileType] = useState<AtlasGqlFileFormat>(AtlasGqlFileFormat.Csv);
  const [inspectionUploadEventTypes, setInspectionUploadEventTypes] = useState(() =>
    getBulkUploadEventTypes(CSV_BULK_UPLOAD_OPTIONS.CSV_BULK)
  );
  const [likelyDelimiterError, setLikelyDelimiterError] = useState<boolean>(false);
  const [cellDelimiter, setCellDelimiter] = useState<AtlasGqlFileFieldDelimiter>();
  const [decimalDelimiter, setDecimalDelimiter] = useState<AtlasGqlDecimalSeparator>(
    AtlasGqlDecimalSeparator.Period
  );
  const [uploadIdsWithNoTaskId, setUploadIdsWithNoTaskId] = useState<string[]>();

  const { refetch } = useServerDataTableContext().context;
  const { setAlertState, setErroredUploadSteps, setCreatedInspectionIds, setCreatedTaskIds } =
    useUploadInspectionDataModalContext();

  const { message } = useMessage();
  const { setSelectedFiles, setUploading } = useUploadContext();

  const { data: inspectionMetadataFieldsData } = useGetInspectionMetadataFieldsQuery();
  const inspectionMetadataFields = orderBy(
    inspectionMetadataFieldsData?.inspectionMetadataFields ?? [],
    'defaultOrder'
  );

  const internalFields = useMemo(() => {
    const fields = uniqBy(inspectionMetadataFields, 'internalName');

    if (requireTaskId) {
      return fields.map(field =>
        field.internalName === 'taskId' ? { ...field, required: true } : field
      );
    }

    return fields;
  }, [inspectionMetadataFields, requireTaskId]);

  const idsOfTasksWithoutInspections = (inspectionTasks ?? [])
    .filter(({ inspectionId }) => !inspectionId)
    .map(({ taskId }) => taskId);
  const inspectionTasksWithInspectionIds: { taskId: string; inspectionId: string }[] = (
    inspectionTasks ?? []
  ).filter((t): t is { taskId: string; inspectionId: string } => !!t.inspectionId);

  const { data: existingTaskData } = useGetTasksForInspectionUploadQuery({
    variables: {
      filterBy: [{ key: 'id', values: idsOfTasksWithoutInspections }],
    },
    skip: !idsOfTasksWithoutInspections.length,
  });
  const hasExistingTaskData = existingTaskData?.paginatedTasks?.items.length;

  const { exampleCsv, exampleJson } = useMemo(() => {
    if (internalFields.length === 0) {
      return { exampleCsv: '', exampleJson: '' };
    }

    let exampleInspections: ExampleInspection[] = EXAMPLE_INSPECTIONS;
    if (hasExistingTaskData) {
      exampleInspections = existingTaskData.paginatedTasks!.items.map(
        ({ id, asset }: AtlasGqlInspectionTask) => ({
          id: '',
          taskId: id,
          description: '',
          inspectionDate: '',
          typeName: '',
          siteName:
            (asset.ancestors?.find(a => a?.assetType?.name === 'Site') as AtlasGqlSite)?.name ?? '',
          turbineName:
            (asset.assetType?.name === 'Turbine'
              ? (asset as AtlasGqlTurbine).name
              : (asset?.ancestors?.find(a => a?.assetType?.name === 'Turbine') as AtlasGqlTurbine)
                  ?.name) ?? '',
          ...(asset.assetType?.name === 'Turbine'
            ? { asset: '', assetType: '' }
            : {
                asset: getUniqueIdentifier(asset),
                assetType: asset.assetType?.name ?? '',
              }),
          vendor: '',
        })
      );
    }

    const csvDeleteNote = 'DELETE THIS ROW BEFORE UPLOADING';
    const exampleCsv =
      'Notes,' +
      internalFields.map(field => field.defaultCsvHeader ?? field.internalName).join(',') +
      '\n' +
      `Explanation: ${csvDeleteNote},` +
      internalFields.map(field => `"${field.explanation?.replace(/\"/g, '""') ?? ''}"`).join(',') +
      '\n' +
      `Valid Data Options: ${csvDeleteNote},` +
      internalFields.map(field => `"${field.validOptions?.replace(/\"/g, '""') ?? ''}"`).join(',') +
      '\n' +
      exampleInspections
        .map(
          (inspection, i) =>
            (hasExistingTaskData ? ',' : `Example ${i + 1}: ${csvDeleteNote},`) +
            internalFields
              .map(field => inspection[field.internalName as keyof typeof inspection])
              .join(',')
        )
        .join('\n');

    const jsonDeleteNote = 'DELETE THIS ENTRY BEFORE UPLOADING';
    const exampleData = [
      Object.fromEntries([
        ['Notes', `Explanation and Valid Data Options: ${jsonDeleteNote}`],
        ...internalFields.map(field => [
          field.defaultJsonFieldName ?? field.internalName,
          `${field.explanation ?? ''}${field.validOptions ? ` ${field.validOptions}` : ''}`,
        ]),
      ]),
      ...exampleInspections.map((inspection, i) =>
        Object.fromEntries([
          ['Notes', `Example ${i + 1}: ${jsonDeleteNote}`],
          ...internalFields.map(field => [
            field.defaultJsonFieldName ?? field.internalName,
            inspection[field.internalName as keyof typeof inspection],
          ]),
        ])
      ),
    ];
    const exampleJson = `data:application/json,${JSON.stringify(exampleData)}`;

    return { exampleCsv, exampleJson };
  }, [internalFields, existingTaskData?.paginatedTasks, hasExistingTaskData]);

  const parseFile = useCallback(
    async (file: File) => {
      if (file.type === 'text/csv') {
        const csvText = await file.text();

        const { meta, data, delimiter, likelyDelimiterError, error } = parseCsv(
          csvText,
          cellDelimiter
        );

        /**
         * If there is a mismatch in columns between the headers and data that does not
         * appear to be due to a non-comma delimiter, prompt the user to reupload.
         *
         * We force the user to select delimiter (rather than using the built-in)
         */
        if (error) {
          setAlertState({
            alertDescription: error,
            alertType: 'error',
          });
        } else if (data.length > INSPECTION_UPLOAD_LIMIT) {
          setAlertState({
            alertDescription: `${data.length} inspections found. Cannot upload more than ${INSPECTION_UPLOAD_LIMIT} inspections at a time. Please create separate files of at most ${INSPECTION_UPLOAD_LIMIT} inspections and try again.`,
            alertType: 'error',
          });
        } else {
          setAlertState(null);
          setProvidedFieldNames(meta.fields.filter(field => field !== 'Notes'));
          setPreviewRecords(data);
          setInspectionCount(data.length);
          setFileType(AtlasGqlFileFormat.Csv);
          setCellDelimiter(delimiter);
          setLikelyDelimiterError(likelyDelimiterError);
        }
      } else if (file.type === 'application/json') {
        const jsonText = await file.text();

        try {
          const parsedJson = flatParseJSON(jsonText);
          /**
           * This assumes (for now) that all records are the same format and are
           * simple key/value pairs. Based on the requirements, this seems fine
           * for now (especially since we have an example file). We may want to support
           * more options in the future.
           */
          if (isEmpty(parsedJson)) {
            setAlertState({
              alertDescription: 'File contained no data. Please check your file and reupload.',
              alertType: 'error',
            });
          } else if (Array.isArray(parsedJson) && parsedJson.length) {
            if (parsedJson.length > INSPECTION_UPLOAD_LIMIT) {
              setAlertState({
                alertDescription: `${parsedJson.length} inspections found. Cannot upload more than ${INSPECTION_UPLOAD_LIMIT} inspections at a time. Please create separate files of at most ${INSPECTION_UPLOAD_LIMIT} inspections and try again.`,
                alertType: 'error',
              });
            } else {
              setAlertState(null);
              setProvidedFieldNames(getProvidedFieldNamesFromJsonRecords(parsedJson));
              setPreviewRecords(parsedJson);
              setInspectionCount(parsedJson.length);
              setFileType(AtlasGqlFileFormat.Json);
            }
          }
        } catch (error) {
          message.error('Error parsing JSON. Please verify formatting and try again.');
        }
      } else {
        message.error('File type not supported. Please upload a .csv or .json file.');
      }
    },
    [cellDelimiter]
  );

  const handleUpload = ({ file }: { file: File; fileList: File[] }) => {
    setFile(file);
    parseFile(file);
  };

  useEffect(() => {
    if (file) {
      parseFile(file);
    }
  }, [cellDelimiter]);

  useEffect(() => {
    const inspectionType = {
      [AtlasGqlFileFormat.Csv]: CSV_BULK_UPLOAD_OPTIONS.CSV_BULK,
      [AtlasGqlFileFormat.Json]: JSON_BULK_UPLOAD_OPTIONS.JSON_BULK,
    }[fileType];

    setInspectionUploadEventTypes(() => getBulkUploadEventTypes(inspectionType));
  }, [fileType]);

  useEffect(() => {
    if (providedFieldNames?.length) {
      setStep(Steps.SummaryMapping);
    }
  }, [providedFieldNames]);

  useEffect(() => {
    /**
     * if field mapping for task id DNE, show message about task creation
     * if field mapping for task id does exist, show message about task creation if some previewRecords are missing the property
     * set upload ids in state for the tasks to be created (to be used to set created task ids after creation, which can be used for rollback)
     */
    if (fieldMappings?.length) {
      const uploadIdMapping = fieldMappings?.find(
        ({ targetFieldName }) => targetFieldName === 'id'
      );
      const taskIdMapping = fieldMappings?.find(
        ({ targetFieldName }) => targetFieldName === 'taskId'
      );

      if (taskIdMapping) {
        const rowsWithoutTaskIds = previewRecords.filter(row =>
          isEmpty(row[taskIdMapping.sourceFieldName])
        );

        if (rowsWithoutTaskIds.length) {
          setAlertState({
            alertType: 'warning',
            alertDescription: `Task ID missing for ${rowsWithoutTaskIds.length} Inspections. Inspection Tasks will be created for and linked to each of these Inspections.`,
          });
          if (uploadIdMapping) {
            setUploadIdsWithNoTaskId(
              rowsWithoutTaskIds.map(row => row[uploadIdMapping.sourceFieldName])
            );
          }
        }
      } else {
        if (uploadIdMapping) {
          setAlertState({
            alertType: 'warning',
            alertDescription:
              'Task ID column was not mapped. Inspection Tasks will be created and linked to each Inspection.',
          });
          setUploadIdsWithNoTaskId(previewRecords.map(row => row[uploadIdMapping.sourceFieldName]));
        } else {
          setAlertState({
            alertType: 'error',
            alertDescription:
              'At least one of ID/Task ID must be mapped. Proceeding will not create any Inspections.',
          });
        }
      }
    }
  }, [previewRecords, fieldMappings]);

  const { getIngestUploadUrls } = useGetIngestUploadUrls();
  const [processMetadataFile] = useProcessInspectionMetadataFileMutation();

  const handleComplete = async () => {
    if (file) {
      setUploading(true);

      const dataType = inspectionUploadEventTypes.inspectionType;
      const fileName = file.name;

      // Get signed url for upload
      const { items } = await getIngestUploadUrls({
        dataType,
        files: [{ fileName }],
        prefix: dataType,
        recordId: uploadId,
      });
      const [uploadItem] = items;

      if (!uploadItem) {
        message.error('Error uploading inspections: Upload URL could not be obtained');
        setUploading(false);
        return;
      }

      setSelectedFiles([
        {
          name: fileName,
          path: '',
          percent: 0,
          sourceName: fileName,
          type: 'file',
          valid: true,
        },
      ]);

      try {
        // Upload file to S3
        await uploadIngestFiles({
          items: [{ ...uploadItem, file }],
        });
      } catch (error) {
        message.error(`Error uploading inspections: ${error.message}`);
        setUploading(false);
        return;
      }

      // Once the file is in S3, call mutation to process
      const { data } = await processMetadataFile({
        variables: {
          input: {
            uploadId,
            key: uploadItem.fields.key,
            bucket: uploadItem.fields.bucket,
            fileFormat: fileType,
            decimalSeparator: decimalDelimiter,
            delimiter: cellDelimiter,
            fieldMappings,
          },
        },
      });
      setUploading(false);

      // conditonally refetch the inspection list
      refetch && (await refetch());

      setValidationStepProps({
        processInspectionMetadataFile: data?.processInspectionMetadataFile,
        fileName: file?.name ?? 'your summary file',
      });
      setStep(Steps.SummaryValidation);
      setErroredUploadSteps(uploadErrors => ({
        ...uploadErrors,
        summary: (data?.processInspectionMetadataFile?.failureCount ?? 0) > 0,
      }));
      setCreatedInspectionIds(
        (data?.processInspectionMetadataFile?.inspections ?? []).map(({ id }) => id)
      );

      // use fileUploadId to figure out which tasks were created here, set those ids in state
      if (uploadIdsWithNoTaskId?.length) {
        const inspectionsWithNewlyCreatedTasks =
          data?.processInspectionMetadataFile?.inspections.filter(
            ({ fileUploadId, task }) =>
              task?.id &&
              fileUploadId?.split('_')[1] &&
              uploadIdsWithNoTaskId.includes(fileUploadId.split('_')[1])
          );
        if (inspectionsWithNewlyCreatedTasks?.length) {
          setCreatedTaskIds(inspectionsWithNewlyCreatedTasks?.map(({ task }) => task!.id));
        }
      }
    }
  };

  const { CSVDownloader } = useCSVDownloader();
  // Declaring a styled component inside component because CSVDownloader must be desctructured from useCSVDownloader
  const StyledCSVDownloader = styled(CSVDownloader)<{ marginRight?: string }>`
    background: transparent;
    border: 0;

    ${({ marginRight }) => marginRight && `margin-right: ${marginRight}`}
  `;

  return (
    <div>
      {[Steps.SummaryUpload, Steps.SummaryMapping].includes(step) && (
        <>
          {inspectionTasksWithInspectionIds.length ? (
            <p>
              <InfoCircleTwoTone twoToneColor={theme.lightBlue} />{' '}
              <span>
                {inspectionTasksWithInspectionIds.length} selected Task
                {inspectionTasksWithInspectionIds.length > 1
                  ? 's already have associated Inspections'
                  : ' already has an associated Inspection'}
                .
              </span>{' '}
              You may add Images to existing Inspections in an upcoming step.
            </p>
          ) : null}
          {idsOfTasksWithoutInspections.length ? (
            <p>
              {' '}
              <strong>
                Create Inspection
                {idsOfTasksWithoutInspections.length > 1 ? 's' : ''} for{' '}
                {idsOfTasksWithoutInspections.length} Task
                {idsOfTasksWithoutInspections.length > 1 ? 's' : ''}
              </strong>
              <br />
            </p>
          ) : null}
          <p>
            An Inspection Summary file is required for bulk inspection upload. This file provides
            inspection-level data such as Asset relationships, description, and date. Up to{' '}
            {INSPECTION_UPLOAD_LIMIT} Inspections may be uploaded at a time.
          </p>

          {!hasExistingTaskData && !!(exampleCsv.length || exampleJson.length) && (
            <p>
              Click to download a sample summary file in{' '}
              <StyledCSVDownloader data={exampleCsv} filename={'summary_example'} type={'link'}>
                csv
              </StyledCSVDownloader>{' '}
              or{' '}
              <a download="summary_example.json" href={exampleJson}>
                json
              </a>
              .
            </p>
          )}
        </>
      )}

      {step === Steps.SummaryUpload && (
        <>
          <Upload
            accept={'.csv,.json'}
            beforeUpload={() => false} // bypass antd callbacks; handle change ourselves
            // @ts-ignore - tedious antd types; this is fine
            onChange={handleUpload}
          >
            <Button className="testid-upload-multiple-inspection-summary" icon={<UploadOutlined />}>
              Upload Summary
            </Button>
          </Upload>
          <br />
          {hasExistingTaskData && (
            <>
              <p>
                If you do not have a summary file to create Inspections for these Tasks, you can
                generate one to edit that is pre-populated with Task and Asset information using one
                of the options below.
                <br />
                <br />
                <StyledCSVDownloader
                  data={exampleCsv}
                  filename={'summary'}
                  type={'link'}
                  marginRight={'0.5rem'}
                >
                  <Button icon={<DownloadOutlined />}>summary.csv</Button>
                </StyledCSVDownloader>
                <Button icon={<DownloadOutlined />} download="summary.json" href={exampleJson}>
                  summary.json
                </Button>
              </p>
            </>
          )}
          {requireTaskId && (
            <WarningAlert message="Rows providing details for Inspection Tasks that already have an Inspection linked will be discarded." />
          )}
        </>
      )}
      {step === Steps.SummaryMapping && (
        <>
          <Divider />
          <Row gutter={{ sm: 16 }}>
            <Col flex="50%">
              <h3>
                Selected file: <strong>{file?.name}</strong>
              </h3>
              <p>Select a source column for each Summary attribute</p>
            </Col>
          </Row>
          <FieldMappingForm
            fieldMappings={fieldMappings}
            fileType={fileType}
            internalFields={internalFields}
            requireAtLeastOneOf={[['id', 'taskId']]}
            providedFieldNames={providedFieldNames ?? []}
            previousStep={Steps.SummaryUpload}
            nextStep={Steps.SummaryPreview}
            setStep={setStep}
            setFieldMappings={setFieldMappings}
            likelyDelimiterError={likelyDelimiterError}
            cellDelimiter={cellDelimiter ?? AtlasGqlFileFieldDelimiter.Comma}
            setCellDelimiter={setCellDelimiter}
            decimalDelimiter={decimalDelimiter}
            setDecimalDelimiter={setDecimalDelimiter}
          />
        </>
      )}
      {step === Steps.SummaryPreview && fieldMappings && (
        <Preview
          inspectionCount={inspectionCount}
          inspectionType={inspectionType}
          internalFields={internalFields}
          fieldMappings={fieldMappings}
          records={previewRecords}
          setStep={setStep}
          handleComplete={handleComplete}
        />
      )}
    </div>
  );
};
